import { supabase } from '@/integrations/supabase/client';

// Public therapist service that uses the secure public table
// This service excludes sensitive information like exact addresses, GPS coordinates, and license numbers
export const publicTherapistService = {
  // Get basic therapist information for public display
  async getPublicTherapistById(id: string) {
    const { data, error } = await supabase
      .from('therapists_public')
      .select(`
        *,
        profile:profiles(first_name, last_name, avatar_url),
        therapist_specialties(
          specialty:specialties(name, description)
        ),
        therapist_availability(*)
      `)
      .eq('id', id)
      .eq('is_verified', true)
      .single();

    return { data, error };
  },

  // Search therapists using the secure public table
  async searchPublicTherapists(filters: {
    city?: string;
    country?: string;
    specialty?: string;
    language?: string;
    is_online_available?: boolean;
    is_in_person_available?: boolean;
    min_rating?: number;
  }) {
    let query = supabase
      .from('therapists_public')
      .select(`
        *,
        profile:profiles(first_name, last_name, avatar_url),
        therapist_specialties(
          specialty:specialties(name, description)
        )
      `)
      .eq('is_verified', true)
      .eq('is_active', true);

    if (filters.city) {
      query = query.ilike('city', `%${filters.city}%`);
    }

    if (filters.country) {
      query = query.ilike('country', `%${filters.country}%`);
    }

    if (filters.is_online_available !== undefined) {
      query = query.eq('is_online_available', filters.is_online_available);
    }

    if (filters.is_in_person_available !== undefined) {
      query = query.eq('is_in_person_available', filters.is_in_person_available);
    }

    if (filters.min_rating) {
      query = query.gte('rating', filters.min_rating);
    }

    if (filters.language) {
      query = query.contains('languages', [filters.language]);
    }

    const { data, error } = await query.order('rating', { ascending: false });

    return { data, error };
  },

  // Get therapists by location (public info only)
  async getTherapistsByLocation(city?: string, country?: string) {
    let query = supabase
      .from('therapists_public')
      .select(`
        *,
        profile:profiles(first_name, last_name, avatar_url)
      `);

    if (city) {
      query = query.ilike('city', `%${city}%`);
    }

    if (country) {
      query = query.ilike('country', `%${country}%`);
    }

    const { data, error } = await query
      .order('rating', { ascending: false })
      .limit(20);

    return { data, error };
  },

  // Get therapists by specialty (public info only)
  async getTherapistsBySpecialty(specialtyId: string) {
    const { data, error } = await supabase
      .from('therapist_specialties')
      .select(`
        therapist:therapists_public(
          *,
          profile:profiles(first_name, last_name, avatar_url)
        )
      `)
      .eq('specialty_id', specialtyId);

    return { data, error };
  },

  // Get therapists by city name
  async getTherapistsByCity(cityName: string, country?: string) {
    let query = supabase
      .from('therapists_public')
      .select(`
        *,
        profile:profiles(first_name, last_name, email, avatar_url),
        therapist_specialties(
          specialty:specialties(name, category)
        )
      `)
      .eq('is_active', true)
      .eq('is_verified', true)
      .ilike('city', `%${cityName}%`);

    if (country) {
      query = query.eq('country', country);
    }

    const { data, error } = await query
      .order('rating', { ascending: false })
      .order('total_reviews', { ascending: false })
      .limit(20);

    return { data, error };
  },

  // Get top therapists by specialty category and city
  async getTherapistsBySpecialtyAndCity(
    cityName: string, 
    category: string, 
    limit: number = 10
  ) {
    const { data, error } = await supabase
      .from('therapists_public')
      .select(`
        *,
        profile:profiles(first_name, last_name, avatar_url),
        therapist_specialties!inner(
          specialty:specialties!inner(name, category)
        )
      `)
      .eq('is_active', true)
      .eq('is_verified', true)
      .ilike('city', `%${cityName}%`)
      .eq('therapist_specialties.specialty.category', category)
      .order('rating', { ascending: false })
      .order('total_reviews', { ascending: false })
      .limit(limit);

    return { data, error };
  }
};